import { useEffect, useRef, useState } from "react";
import { CircleAlert, CircleCheck, ImageUp, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import FileDropzone from "@/components/FileDropzone";
import Spinner from "@/components/Spinner";
import BrickTypeToggle from "./BrickTypeToggle";
import {
  ACCEPTED_IMAGE_TYPES,
  keepValidImages,
  updateBrickSeries,
  uploadImage,
} from "../../utils";
import useOrganization from "../../hooks/useOrganization";
import type { BrickSeries, BrickType } from "../../masterImages/types";

interface Props {
  brickSeries: BrickSeries;
  open: boolean;
  onClose(): void;
  onReload(): void;
}

interface Item {
  id: string;
  file: File;
  preview: string;
  type: BrickType;
  progress: number;
  key: string | null;
  error: string | null;
}

// Each file uploads as soon as it is added; "Toevoegen" saves them to the series.
const UploadBricksModal: React.FC<Props> = ({ brickSeries, open, onClose, onReload }) => {
  const organization = useOrganization();
  const [items, setItems] = useState<Item[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const update = (id: string, patch: Partial<Item>) =>
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...patch } : item)));

  useEffect(() => {
    if (!open) {
      itemsRef.current.forEach((item) => URL.revokeObjectURL(item.preview));
      setItems([]);
      setSubmitting(false);
    }
  }, [open]);

  const add = async (files: File[]) => {
    const valid = await keepValidImages(files);
    const added = valid.map<Item>((file) => ({
      id: crypto.randomUUID(),
      file,
      preview: URL.createObjectURL(file),
      type: "full",
      progress: 0,
      key: null,
      error: null,
    }));
    setItems((current) => [...current, ...added]);
    added.forEach(async (item) => {
      try {
        const key = await uploadImage(
          organization,
          brickSeries._id,
          "brick",
          item.file,
          (progress) => update(item.id, { progress }),
        );
        update(item.id, { key, progress: 100 });
      } catch (e) {
        update(item.id, { error: (e as Error).message || "Uploaden mislukt" });
      }
    });
  };

  const remove = (item: Item) => {
    URL.revokeObjectURL(item.preview);
    setItems((current) => current.filter((i) => i.id !== item.id));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await updateBrickSeries(organization, brickSeries._id, {
        bricks: [
          ...brickSeries.bricks,
          ...items.map((item) => ({ type: item.type, image: item.key })),
        ],
      });
      toast.success(`${items.length} ${items.length === 1 ? "steen" : "stenen"} toegevoegd`);
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
      onReload();
      setSubmitting(false);
    }
  };

  const uploading = items.filter((item) => item.key == null && item.error == null).length;
  const failed = items.filter((item) => item.error != null).length;
  const ready = items.length > 0 && uploading === 0 && failed === 0;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !submitting && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Stenen uploaden</DialogTitle>
          <DialogDescription>
            Eén foto per steen, uitgesneden en het liefst met een transparante achtergrond. Kies per
            steen of het een hele of halve steen is.
          </DialogDescription>
        </DialogHeader>
        <FileDropzone
          accept={ACCEPTED_IMAGE_TYPES}
          multiple
          disabled={submitting}
          onFiles={add}
          className="px-6 py-8"
        >
          <ImageUp className="size-6" />
          <span className="font-medium text-foreground">Klik of sleep bestanden hierheen</span>
          <span>PNG, JPEG of WebP, tot 2 MB per bestand.</span>
        </FileDropzone>
        {items.length > 0 && (
          <ul className="-mx-2 grid max-h-80 gap-1 overflow-y-auto px-2">
            {items.map((item) => (
              <li key={item.id} className="flex items-center gap-3 rounded-lg border p-2">
                <span className="checkerboard flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md">
                  <img src={item.preview} alt="" className="max-h-full max-w-full object-contain" />
                </span>
                <div className="grid min-w-0 flex-1 gap-1">
                  <span className="truncate text-sm font-medium">{item.file.name}</span>
                  {item.error ? (
                    <span className="flex items-center gap-1 text-xs text-destructive">
                      <CircleAlert className="size-3.5 shrink-0" />
                      <span className="truncate">{item.error}</span>
                    </span>
                  ) : item.key ? (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <CircleCheck className="size-3.5 text-emerald-600" />
                      Geüpload
                    </span>
                  ) : (
                    <Progress value={item.progress} className="h-1.5" aria-label="Uploaden" />
                  )}
                </div>
                <BrickTypeToggle
                  value={item.type}
                  onChange={(type) => update(item.id, { type })}
                  disabled={submitting}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => remove(item)}
                  disabled={submitting}
                  aria-label={`${item.file.name} verwijderen`}
                >
                  <X />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter className="items-center">
          {failed > 0 && (
            <span className="mr-auto text-sm text-destructive">
              Verwijder de mislukte {failed === 1 ? "upload" : "uploads"} om door te gaan.
            </span>
          )}
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Annuleren
          </Button>
          <Button onClick={submit} disabled={!ready || submitting}>
            {(submitting || uploading > 0) && <Spinner />}
            {items.length > 1 ? `${items.length} stenen toevoegen` : "Toevoegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadBricksModal;
